import { businessConnectionMst } from '@libs/domain';
import type { FieldErrorField } from '@ps-smart-cp/ppfw-client';
import { FieldSet, TextNumberField, kebabCase, useItemName, useValidator } from '@ps-smart-cp/ppfw-client';
import type { Ref } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useMstSettings, useOperationControl } from '../../../context';
import { businessConnectionMstSettingFieldConfig } from '../item-config';
import { registerSetting } from '../repositories';

interface BusinessConnectionSettingFieldSetProps {
  errorFields?: string[];
}

interface BusinessConnectionSettingFieldSetRef {
  validate: () => FieldErrorField[];
  register: () => Promise<void>;
}

function InnerComponent(
  props: BusinessConnectionSettingFieldSetProps,
  ref: Ref<BusinessConnectionSettingFieldSetRef>,
) {
  const getName = useItemName();
  const { businessConnection } = useMstSettings();
  const [setting, setSetting] = useState(businessConnection);
  const validator = useValidator();
  const validate = () =>
    validator
      .with(setting, businessConnectionMstSettingFieldConfig)
      .execute()
      .map((s) => ({ ...s, field: kebabCase(`business-connection-${s.field}`) }));
  const register = async () => registerSetting(setting);
  const { isStarted } = useOperationControl();

  useImperativeHandle(ref, () => ({ validate, register }), [setting]);

  return (
    <FieldSet
      legendProps={{
        title: getName(businessConnectionMst.labelNames.businessConnection),
        id: 'business-connection-field-set',
      }}
    >
      <TextNumberField
        id="business-connection-code-digits"
        obj={setting}
        setObj={setSetting}
        prop="codeDigits"
        items={businessConnectionMstSettingFieldConfig}
        errorFields={props.errorFields}
        disabled={isStarted}
      />
    </FieldSet>
  );
}

export const BusinessConnectionSettingFieldSet = forwardRef(InnerComponent);
export type { BusinessConnectionSettingFieldSetRef };
